import React, { useState, useEffect, useCallback, useRef, useMemo, Fragment } from "react";
import {
    Alert,
    Box,
    Typography,
    Button,
    CircularProgress,
    Collapse,
    Switch,
    IconButton,
    Paper,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    FormControl,
    FormLabel,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { BarChart } from "@mui/x-charts";
import {
    ArrowBack as ArrowBackIcon,
    Send as SendIcon,
    Summarize as SummarizeIcon,
    TrendingUp as TrendingUpIcon,
    Psychology as PsychologyIcon,
    EmojiObjects as EmojiObjectsIcon,
    OpenInNew as OpenInNewIcon,
    Download as DownloadIcon,
    AssessmentOutlined as AssessmentOutlinedIcon,
    TipsAndUpdatesOutlined as TipsAndUpdatesOutlinedIcon,
    ReportProblemOutlined as ReportProblemOutlinedIcon,
    ExpandMore as ExpandMoreIcon,
    Add as AddIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { token_keys } from "../../constants";
import insightsApi from "../../api/insightsApi";
import {
    riskBand,
    signalBand,
    signalToRisk,
    ScoreScaleInfo,
    ScoreScaleLegend,
} from "../../components/Insight/RisksSection";
import RiskScoreCards from "../../components/Insight/RiskScoreCards";
import AnswerExportMenu from "../../components/Insight/AnswerExportMenu";
import {
    exportAnswerAsPdf,
    exportAnswerAsWord,
    exportTimestamp,
} from "../../components/Insight/answerExport";
import useChatLlmProvider from "../../hooks/useChatLlmProvider";
import ChatSettingsButton from "../../components/Insight/ChatSettingsButton";
import {
    buildSourcesPayload,
    hasSources,
    selectedCount,
} from "../../components/Insight/chatSources";
import HistoryIcon from "@mui/icons-material/History";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    Divider,
    Chip
} from "@mui/material";
import useStore from "../../store";
import createChunkReplay from "../../components/Insight/chunkReplay";
import TypingIndicator from "../../components/Insight/TypingIndicator";
import DocumentsPage from "../Documents/DocumentsPage";
import ImpactsWorkspacePage from "../impacts/ImpactsWorkspacePage";
import NewAssessmentDialog from "../../components/Project/NewAssessmentDialog";
import { canEditProjectContent } from "../../utils/projectRoles";

// ── Shared palette ─────────────────────────────────────────────────────────────
// First two entries preserve the original wave1/wave2 colours; the rest extend
// the palette so any number of reports get a distinct, stable colour.
const COMPARE_COLORS = [
    "#1565c0", "#64b5f6", "#2e7d32", "#e65100",
    "#6a1b9a", "#00695c", "#c62828", "#f9a825",
    "#0288d1", "#7cb342", "#5e35b1", "#d81b60",
];
const colorForIndex = (i) => COMPARE_COLORS[i % COMPARE_COLORS.length];

const reportKey = (id) => `r_${id}`;
const findValue = (values, reportId) =>
    (values || []).find((v) => String(v.report_id) === String(reportId));

const formatPct = (v) => (v == null ? null : `${v > 0 ? "+" : ""}${Number(v).toFixed(1)}%`);

// Nothing risk-related is worth offering when risk_filters carries no options:
// the prediction endpoint has nothing to slice, so the radar, the bars and the
// risk Q&A would all answer from an empty set.
const hasRiskFilters = (rf) =>
    rf != null && typeof rf === "object" && Object.keys(rf).length > 0;

// ── Left-hand tabs ────────────────────────────────────────────────────────────
// Impacts, Evidence and Documents are all wired up now; EmptyPanel stays for
// whatever tab lands here next.
// `key` is the domain name (these are stakeholder groups elsewhere in the app);
// `label` is what clients see. Renaming a tab is a label change only.
const LEFT_TABS = [
    { key: "impacts", labelId: "projects.impacts_tab", label: "Impacts" },
    { key: "evidence", labelId: "projects.evidence_tab", label: "Evidence" },
    { key: "documents", labelId: "projects.documents_tab", label: "Documents" },
];

// The tab the page lands on. Addressed by key, not index, so reordering
// LEFT_TABS can never silently change where the page opens.
const DEFAULT_TAB = Math.max(0, LEFT_TABS.findIndex((t) => t.key === "impacts"));

// ── Assessment views ──────────────────────────────────────────────────────────
// What to chart for the waves listed above: the first entry is the default.
const ASSESSMENT_VIEWS = [
    { key: "pillar", labelId: "projects.view_pillar", label: "Pillar" },
    { key: "top_terms", labelId: "projects.view_top_terms", label: "Top terms" },
    { key: "risk", labelId: "projects.view_risk", label: "Risk" },
];

// ── Assessment statuses ───────────────────────────────────────────────────────
// Rows from an older BE carry no status and simply show none.
const ASSESSMENT_STATUSES = {
    draft: { labelId: "survey.status_draft", label: "Draft" },
    active: { labelId: "survey.status_active", label: "Active" },
    closed: { labelId: "survey.status_closed", label: "Closed" },
};

// ── Assessment list ───────────────────────────────────────────────────────────
// The Evidence tab is the assessments linked to the project, with or without a
// report yet; a report shows on its assessment's row. The row itself is inert;
// only a report button navigates, so it is obvious what is clickable. It stays
// an anchor opening a new browser tab, so the project workspace stays put.
// "New assessment" creates a survey linked to the project
// (NewAssessmentDialog); like every other write, it is hidden from viewers.
export function AssessmentList({ assessments, canEdit, projectId }) {
    const intl = useIntl();
    const [newOpen, setNewOpen] = useState(false);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#00438E", flex: 1 }}>
                    <FormattedMessage
                        id="projects.assessments_heading"
                        defaultMessage="Assessments ({count})"
                        values={{ count: assessments.length }}
                    />
                </Typography>
                {canEdit && projectId != null && (
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setNewOpen(true)}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        <FormattedMessage id="projects.new_assessment" defaultMessage="New assessment" />
                    </Button>
                )}
            </Box>
            {canEdit && projectId != null && (
                <NewAssessmentDialog open={newOpen} onClose={() => setNewOpen(false)} projectId={projectId} />
            )}
            {assessments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                    <FormattedMessage
                        id="projects.no_assessments"
                        defaultMessage="No assessments in this project."
                    />
                </Typography>
            ) : (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <List disablePadding>
                        {assessments.map((a, i) => {
                            const statusMeta = ASSESSMENT_STATUSES[a.status];
                            const details = [
                                statusMeta &&
                                    intl.formatMessage({ id: statusMeta.labelId, defaultMessage: statusMeta.label }),
                                a.responses != null &&
                                    intl.formatMessage(
                                        {
                                            id: "projects.assessment_responses",
                                            defaultMessage: "{count, plural, one {# response} other {# responses}}",
                                        },
                                        { count: a.responses }
                                    ),
                            ].filter(Boolean);
                            return (
                            <Fragment key={a.key}>
                                {i > 0 && <Divider component="li" />}
                                <ListItem sx={{ py: 1.25, px: 2, gap: 1, flexWrap: "wrap" }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#00438E" }} noWrap>
                                            {a.title}
                                        </Typography>
                                        {details.length > 0 && (
                                            <Typography variant="caption" color="text.secondary">
                                                {details.join(" · ")}
                                            </Typography>
                                        )}
                                    </Box>
                                    {a.reports.length === 0 ? (
                                        <Typography
                                            variant="caption"
                                            color="text.disabled"
                                            sx={{ flexShrink: 0, minWidth: 104, textAlign: "center" }}
                                        >
                                            <FormattedMessage
                                                id="projects.no_report_yet"
                                                defaultMessage="No report yet"
                                            />
                                        </Typography>
                                    ) : (
                                        a.reports.map((w) => (
                                            <Button
                                                key={w.report_id}
                                                size="small"
                                                variant="outlined"
                                                component="a"
                                                href={`/insights/${w.report_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                // The dot is the wave's colour in the charts below.
                                                startIcon={
                                                    <Box
                                                        component="span"
                                                        sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: w.color }}
                                                    />
                                                }
                                                endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                                                aria-label={intl.formatMessage(
                                                    {
                                                        id: "projects.see_report_for",
                                                        defaultMessage: "See Report for {wave}",
                                                    },
                                                    { wave: `${a.title} (${w.label})` }
                                                )}
                                                sx={{
                                                    flexShrink: 0,
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    borderColor: "rgba(0, 67, 142, 0.4)",
                                                    color: "#00438E",
                                                    "&:hover": {
                                                        borderColor: "#00438E",
                                                        bgcolor: "rgba(0, 67, 142, 0.08)",
                                                    },
                                                }}
                                            >
                                                {/* One report reads as "See Report"; several
                                                    are told apart by their wave. */}
                                                {a.reports.length === 1 ? (
                                                    <FormattedMessage id="projects.see_report" defaultMessage="See Report" />
                                                ) : (
                                                    w.label
                                                )}
                                            </Button>
                                        ))
                                    )}
                                </ListItem>
                            </Fragment>
                            );
                        })}
                    </List>
                </Paper>
            )}
        </Box>
    );
}

// ── Placeholder for tabs and interventions with no data yet ────────────────────
function EmptyPanel({ label }) {
    return (
        <Box
            sx={{
                py: 10,
                px: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 2,
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary" }}>
                {label}
            </Typography>
            <Typography variant="body2" color="text.disabled">
                <FormattedMessage
                    id="projects.panel_empty"
                    defaultMessage="Nothing here yet."
                />
            </Typography>
        </Box>
    );
}

// ── Pillar Grouped Bar Chart ──────────────────────────────────────────────────
function PillarBarChart({ pillars, waves }) {
    const intl = useIntl();
    const list = Array.isArray(pillars) ? pillars : [];

    const dataset = list.map((p) => {
        const row = { pillar: p.name };
        waves.forEach((w) => {
            const v = findValue(p.values, w.report_id);
            row[reportKey(w.report_id)] = parseFloat((v?.value ?? 0).toFixed(4));
        });
        return row;
    });

    if (dataset.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
                <FormattedMessage id="projects.no_pillar_data" defaultMessage="No pillar data available." />
            </Typography>
        );
    }

    const series = waves.map((w, i) => ({
        dataKey: reportKey(w.report_id),
        label: w.label,
        color: w.color ?? colorForIndex(i),
    }));

    return (
        <Box
            sx={{
                background: "linear-gradient(135deg, #f8faff 0%, #eef3fb 100%)",
                borderRadius: 3,
                border: "1px solid rgba(21, 101, 192, 0.12)",
                p: 2,
                pt: 3,
            }}
        >
            <BarChart
                dataset={dataset}
                xAxis={[{
                    scaleType: "band",
                    dataKey: "pillar",
                    tickLabelStyle: {
                        fontSize: 12,
                        fontWeight: 500,
                        fill: "#37474f",
                    },
                    disableLine: true,
                    disableTicks: true,
                }]}
                yAxis={[{
                    label: intl.formatMessage({ id: "projects.sentiment_mean", defaultMessage: "Sentiment Mean" }),
                    labelStyle: { fontSize: 12, fontWeight: 600, fill: "#546e7a" },
                    tickLabelStyle: { fontSize: 11, fill: "#78909c" },
                    disableLine: true,
                    disableTicks: true,
                }]}
                series={series}
                height={440}
                margin={{ left: 75, bottom: 60, right: 30, top: 16 }}
                slotProps={{
                    legend: {
                        direction: "row",
                        position: { vertical: "bottom", horizontal: "middle" },
                        padding: { top: 24, bottom: 0 },
                        itemMarkWidth: 12,
                        itemMarkHeight: 12,
                        markGap: 6,
                        itemGap: 28,
                        labelStyle: { fontSize: 13, fontWeight: 500, fill: "#37474f" },
                    },
                }}
                sx={{
                    "& .MuiChartsGrid-line": {
                        stroke: "rgba(21, 101, 192, 0.08)",
                        strokeDasharray: "4 4",
                    },
                    "& .MuiBarElement-root": {
                        rx: 4,
                        ry: 4,
                    },
                }}
                grid={{ horizontal: true }}
            />
        </Box>
    );
}

// ── Keyword Bump Chart ────────────────────────────────────────────────────────
const BUMP_TOP_N = 20;
const BUMP_PAD = { top: 56, bottom: 24, left: 170, right: 90 };
const BUMP_ROW_H = 26;
const BUMP_COL_GAP = 150;

function KeywordBumpChart({ keywords, waves }) {
    const intl = useIntl();
    const list = Array.isArray(keywords) ? keywords : [];

    if (list.length === 0 || waves.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
                <FormattedMessage id="projects.no_keyword_data" defaultMessage="No keyword data available." />
            </Typography>
        );
    }

    // Keywords arrive pre-sorted by the first report's frequency descending.
    const top = list.slice(0, BUMP_TOP_N);
    const cols = waves.length;
    const n = top.length;

    const svgW = BUMP_PAD.left + BUMP_PAD.right + (cols - 1) * BUMP_COL_GAP;
    const svgH = BUMP_PAD.top + BUMP_PAD.bottom + n * BUMP_ROW_H;
    const colX = waves.map((_, j) =>
        cols > 1 ? BUMP_PAD.left + j * BUMP_COL_GAP : BUMP_PAD.left
    );
    const rowY = (idx) => BUMP_PAD.top + idx * BUMP_ROW_H + BUMP_ROW_H / 2;

    // Per-column vertical position: order the displayed keywords by their rank
    // in that column and assign sequential rows (compact, no rank gaps).
    const yByCol = waves.map((w) => {
        const present = top
            .map((k) => ({ term: k.term, val: findValue(k.values, w.report_id) }))
            .filter((x) => x.val && x.val.frequency != null)
            .sort((a, b) => (a.val.rank ?? 1e9) - (b.val.rank ?? 1e9));
        const map = {};
        present.forEach((x, idx) => { map[x.term] = rowY(idx); });
        return map;
    });

    // Assign each keyword a stable colour so its line can be traced across columns.
    const colorByTerm = {};
    top.forEach((k, i) => { colorByTerm[k.term] = colorForIndex(i); });

    return (
        <Box
            sx={{
                overflowX: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                p: 2,
                background: "linear-gradient(135deg, #f8faff 0%, #eef3fb 100%)",
            }}
        >
            <svg
                width={svgW}
                height={svgH}
                style={{ fontFamily: "inherit", display: "block", margin: "0 auto" }}
            >
                {/* Column headers + guide lines */}
                {waves.map((w, j) => (
                    <g key={`col-${w.report_id}`}>
                        <text
                            x={colX[j]}
                            y={BUMP_PAD.top - 30}
                            textAnchor="middle"
                            fontSize={12}
                            fontWeight="bold"
                            fill={w.color ?? colorForIndex(j)}
                        >
                            {w.label}
                        </text>
                        <text x={colX[j]} y={BUMP_PAD.top - 16} textAnchor="middle" fontSize={10} fill="#999">
                            {intl.formatMessage({ id: "projects.frequency_rank", defaultMessage: "(frequency rank)" })}
                        </text>
                        <line
                            x1={colX[j]}
                            y1={BUMP_PAD.top - 6}
                            x2={colX[j]}
                            y2={svgH - BUMP_PAD.bottom}
                            stroke="#e0e0e0"
                            strokeWidth={1}
                        />
                    </g>
                ))}

                {/* One polyline + nodes per keyword */}
                {top.map((k) => {
                    const color = colorByTerm[k.term];
                    const points = waves
                        .map((w, j) => {
                            const y = yByCol[j][k.term];
                            if (y == null) return null;
                            return { j, x: colX[j], y, val: findValue(k.values, w.report_id) };
                        })
                        .filter(Boolean);

                    if (points.length === 0) return null;

                    const path = points
                        .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
                        .join(" ");
                    const first = points[0];

                    return (
                        <g key={k.term}>
                            {points.length > 1 && (
                                <path d={path} fill="none" stroke={color} strokeWidth={1.5} opacity={0.55} />
                            )}

                            {/* Keyword label at the left of its first node */}
                            <text x={first.x - 12} y={first.y + 4} textAnchor="end" fontSize={11} fill="#333">
                                {k.term}
                            </text>

                            {points.map((p) => {
                                const pct = p.j > 0 ? formatPct(p.val?.pct_change_from_prev) : null;
                                const pctColor =
                                    p.val?.pct_change_from_prev > 0 ? "#2e7d32"
                                        : p.val?.pct_change_from_prev < 0 ? "#c62828"
                                            : "#777";
                                return (
                                    <g key={p.j}>
                                        <circle cx={p.x} cy={p.y} r={3.5} fill={color} />
                                        <text
                                            x={p.x + 8}
                                            y={p.y - 2}
                                            textAnchor="start"
                                            fontSize={11}
                                            fill={color}
                                            fontWeight="bold"
                                        >
                                            {p.val?.frequency}
                                        </text>
                                        {pct && (
                                            <text
                                                x={p.x + 8}
                                                y={p.y + 9}
                                                textAnchor="start"
                                                fontSize={9}
                                                fill={pctColor}
                                            >
                                                {pct}
                                            </text>
                                        )}
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
}

// ── Compare Risks (filters + radar) ───────────────────────────────────────────
function valueToRadius(v, maxR) {
    const clamped = Math.max(0, Math.min(1, v));
    return clamped * maxR;
}

function polygonPoints(values, maxR) {
    const n = values.length;
    return values.map((v, i) => {
        const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        const r = valueToRadius(v, maxR);
        return {
            x: r * Math.cos(angle),
            y: r * Math.sin(angle),
        };
    });
}

function RiskRadarChart({ pillars, series }) {
    const intl = useIntl();
    const [hovered, setHovered] = useState(null);
    const size = 440;
    const cx = size / 2;
    const cy = size / 2;
    const maxR = 150;
    const n = pillars.length;

    const ringFracs = [0.25, 0.5, 0.75, 1];

    const pathFromPts = (pts) =>
        pts.map((p, i) => `${i === 0 ? "M" : "L"} ${cx + p.x} ${cy + p.y}`).join(" ") + " Z";

    // values are risk scores in [0, 1] — 3 dp, matching the Risks tab's radar
    const tooltipLines = (label, values) =>
        [label, ...pillars.map((pillar, i) => `${pillar}: ${Number(values[i] ?? 0).toFixed(3)}`)].join("\n");

    // Hovered series renders last (on top); everything else dims.
    const drawOrder = series
        .map((s, i) => i)
        .sort((a, b) => (a === hovered ? 1 : 0) - (b === hovered ? 1 : 0));

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
            <svg
                viewBox={`0 0 ${size} ${size}`}
                width={size}
                height={size}
                style={{ fontFamily: "inherit", display: "block", maxWidth: "100%", height: "auto" }}
                aria-label={intl.formatMessage({
                    id: "projects.risk_radar_chart_label",
                    defaultMessage: "Risk comparison radar chart",
                })}
            >
                {ringFracs.map((frac) => (
                    <polygon
                        key={frac}
                        points={Array.from({ length: n }, (_, i) => {
                            const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
                            const r = frac * maxR;
                            return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
                        }).join(" ")}
                        fill="none"
                        stroke="rgba(21, 101, 192, 0.14)"
                        strokeWidth={1}
                    />
                ))}
                {Array.from({ length: n }, (_, i) => {
                    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
                    return (
                        <line
                            key={`spoke-${i}`}
                            x1={cx}
                            y1={cy}
                            x2={cx + maxR * Math.cos(angle)}
                            y2={cy + maxR * Math.sin(angle)}
                            stroke="rgba(21, 101, 192, 0.2)"
                            strokeWidth={1}
                        />
                    );
                })}

                {drawOrder.map((idx) => {
                    const s = series[idx];
                    const isHover = hovered === idx;
                    const isDim = hovered != null && hovered !== idx;
                    const pts = polygonPoints(s.values, maxR);
                    const d = pathFromPts(pts);
                    return (
                        <g
                            key={s.label}
                            onMouseEnter={() => setHovered(idx)}
                            onMouseLeave={() => setHovered(null)}
                            style={{ cursor: "pointer" }}
                        >
                            <path
                                d={d}
                                fill={s.color}
                                stroke={s.color}
                                strokeWidth={isHover ? 3 : 2}
                                strokeLinejoin="round"
                                fillOpacity={isDim ? 0.08 : isHover ? 0.4 : 0.22}
                                strokeOpacity={isDim ? 0.4 : 1}
                                style={{
                                    transition: "fill-opacity 0.18s ease, stroke-opacity 0.18s ease, stroke-width 0.18s ease",
                                }}
                            >
                                <title>{tooltipLines(s.label, s.values)}</title>
                            </path>
                            {/* Vertex dots carry the risk band, the way the
                                single-report radar does: the polygon says which
                                wave, the dot says how bad that pillar is. */}
                            {pts.map((pt, pi) => {
                                const band = riskBand(s.values[pi]);
                                return (
                                    <circle
                                        key={pi}
                                        cx={cx + pt.x}
                                        cy={cy + pt.y}
                                        r={isHover ? 5 : 4}
                                        fill={band.color}
                                        stroke="#fff"
                                        strokeWidth={1}
                                        opacity={isDim ? 0.3 : 0.95}
                                        style={{ transition: "opacity 0.18s ease" }}
                                    >
                                        <title>
                                            {`${s.label} — ${pillars[pi]}: ${Number(s.values[pi] ?? 0).toFixed(3)} — ${band.label}`}
                                        </title>
                                    </circle>
                                );
                            })}
                        </g>
                    );
                })}

                {pillars.map((pillar, i) => {
                    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
                    return (
                        <text
                            key={pillar}
                            x={cx + (maxR + 36) * Math.cos(angle)}
                            y={cy + (maxR + 36) * Math.sin(angle)}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={11}
                            fontWeight={600}
                            fill="#37474f"
                        >
                            {pillar}
                        </text>
                    );
                })}
            </svg>
            <Stack direction="row" spacing={3} sx={{ mt: 1, flexWrap: "wrap", justifyContent: "center" }}>
                {series.map((s, idx) => (
                    <Stack
                        key={s.label}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        onMouseEnter={() => setHovered(idx)}
                        onMouseLeave={() => setHovered(null)}
                        sx={{ cursor: "pointer", opacity: hovered != null && hovered !== idx ? 0.5 : 1 }}
                    >
                        <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: s.color }} />
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                            {s.label}
                        </Typography>
                    </Stack>
                ))}
                {/* Polygons are coloured by wave; the vertex dots are banded,
                    so the band swatches do describe something on this chart. */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ width: "100%", justifyContent: "center" }}
                >
                    <ScoreScaleLegend scale="risk" centered />
                    <ScoreScaleInfo scale="risk" />
                </Stack>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ width: "100%", textAlign: "center" }}
                >
                    {intl.formatMessage({
                        id: "projects.risk_radar_scale_hint",
                        defaultMessage: "Scale: center = lowest risk, outer ring = highest risk",
                    })}
                </Typography>
            </Stack>
        </Box>
    );
}

// ── Signal pillar bars ────────────────────────────────────────────────────────
// The radar's data read the other way round. The radar plots RISK in [0, 1]
// (centre = good, outer ring = bad); these bars plot the raw SIGNAL score in
// [-1, 1] where higher is BETTER, growing out of a centre zero line — so a
// negative pillar reads as a bar to the left instead of a dot near the rim.
// One row per wave inside each pillar group: the leading dot names the wave,
// the fill is banded, matching the colour of that wave's radar vertex.
const BAR_LABEL_W = 92;
const BAR_VALUE_W = 62;

function CompareSignalBars({ pillars, series }) {
    const intl = useIntl();

    if (pillars.length === 0 || series.length === 0) return null;

    return (
        <Box sx={{ width: "100%" }}>
            {/* Axis ruler — the bars have no gridlines of their own */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Box sx={{ width: BAR_LABEL_W, flexShrink: 0 }} />
                <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between" }}>
                    {["-100%", "0", "+100%"].map((t) => (
                        <Typography key={t} variant="caption" color="text.disabled">
                            {t}
                        </Typography>
                    ))}
                </Box>
                <Box sx={{ width: BAR_VALUE_W, flexShrink: 0 }} />
            </Box>

            <Stack spacing={2.5}>
                {pillars.map((pillar, pi) => (
                    <Box key={pillar}>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "#0d47a1", mb: 0.75, textTransform: "capitalize" }}
                        >
                            {pillar}
                        </Typography>
                        <Stack spacing={0.75}>
                            {series.map((sr) => {
                                const score = Number(sr.signals?.[pi] ?? 0);
                                const band = signalBand(score);
                                return (
                                    <Box key={sr.label} sx={{ display: "flex", alignItems: "center" }}>
                                        <Box
                                            sx={{
                                                width: BAR_LABEL_W,
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.75,
                                                pr: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 9,
                                                    height: 9,
                                                    borderRadius: "50%",
                                                    bgcolor: sr.color,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {sr.label}
                                            </Typography>
                                        </Box>
                                        <Tooltip
                                            title={`${intl.formatMessage({ id: band.labelId, defaultMessage: band.label })} (${band.range})`}
                                            placement="top"
                                        >
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    flex: 1,
                                                    height: 16,
                                                    bgcolor: "#eceff1",
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: "50%",
                                                        width: `${Math.min(Math.abs(score), 1) * 50}%`,
                                                        height: "100%",
                                                        bgcolor: band.color,
                                                        transform: score < 0 ? "translateX(-100%)" : "none",
                                                        transition: "width 0.3s ease",
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: "50%",
                                                        width: 2,
                                                        height: "100%",
                                                        bgcolor: "#90a4ae",
                                                        transform: "translateX(-50%)",
                                                    }}
                                                />
                                            </Box>
                                        </Tooltip>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                width: BAR_VALUE_W,
                                                flexShrink: 0,
                                                textAlign: "right",
                                                fontWeight: 700,
                                                color: band.color,
                                                fontVariantNumeric: "tabular-nums",
                                            }}
                                        >
                                            {`${score > 0 ? "+" : ""}${(score * 100).toFixed(0)}%`}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                ))}
            </Stack>

            <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ mt: 2, justifyContent: "center" }}
            >
                <ScoreScaleLegend scale="signal" centered />
                <ScoreScaleInfo scale="signal" />
            </Stack>
        </Box>
    );
}

/** Map the prediction API's per-pillar values onto the radar's risk scale.
 *  The API returns SIGNAL scores in [-1, 1] where higher is BETTER; the radar
 *  plots RISK in [0, 1] where higher is WORSE (centre = 0, outer ring = 1).
 *  Same conversion the single-report Risks tab uses, so the two agree.
 *  (This used to clamp the raw value into [0, 1], which floored every negative
 *  — i.e. every genuinely bad — pillar at the centre of the chart.)
 */
function normalizeForRadar(scores, pillars) {
    return pillars.map((p) => Math.max(0, Math.min(1, signalToRisk(scores[p] ?? 0))));
}

// ── Risk-summary visuals for a chat answer ────────────────────────────
// The BE's risk stream sends a risk_meta frame before the answer tokens. On a
// comparison it carries `reports[]` — one entry per compared report, each with
// its own per-pillar scores — plus the primary scores it already sent for a
// single report. Scores are SIGNAL values in [-1, 1] (higher is better) and are
// banded as-is by the score cards, exactly like the Risks tab.
// Cards only: the per-wave radars this used to draw are gone, so a risk answer
// here shows the same visuals as the single-report Ask Prompta chat.
function RiskMetaPanels({ riskMeta }) {
    const perReport = (riskMeta?.reports || []).filter(
        (r) => r && r.scores && Object.keys(r.scores).length > 0
    );

    // No per-report breakdown (single-report shape, or a comparison the BE
    // could only score as a whole) — fall back to the primary score cards.
    if (perReport.length === 0) {
        const scores = riskMeta?.scores;
        if (!scores || Object.keys(scores).length === 0) return null;
        return <RiskScoreCards scores={scores} />;
    }

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: perReport.length > 1 ? "1fr 1fr" : "1fr" },
                gap: 2,
                mb: 1.5,
            }}
        >
            {perReport.map((r, i) => (
                <Box key={r.report_id ?? i}>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: "#0d47a1", mb: 1, textAlign: "center" }}
                    >
                        {r.label || `Wave ${r.wave ?? i + 1}`}
                    </Typography>
                    <RiskScoreCards scores={r.scores} />
                </Box>
            ))}
        </Box>
    );
}

function CompareRisksPanel({ waves, riskFilters }) {
    const intl = useIntl();
    const filterKeys = riskFilters ? Object.keys(riskFilters) : [];
    const reportIds = useMemo(() => waves.map((w) => w.report_id), [waves]);

    const [selectedFilters, setSelectedFilters] = useState({});
    const [radarData, setRadarData] = useState(null);
    const [showBars, setShowBars] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // AI comparison-insight state (mirrors RisksSection's single-report flow)
    const [insightOpen, setInsightOpen] = useState(false);
    const [insightLoading, setInsightLoading] = useState(false);
    const [insightError, setInsightError] = useState(null);
    const [insightData, setInsightData] = useState(null);
    const isInitialMount = useRef(true);
    const debounceRef = useRef(null);
    const streamAbortRef = useRef(null);

    // Build the active-filter object the same way fetchScores does:
    // only keys with a non-empty selection are included.
    const buildActiveFilters = (filters) => {
        const activeFilters = {};
        Object.entries(filters).forEach(([k, v]) => {
            if (v && v.length > 0) activeFilters[k] = v;
        });
        return activeFilters;
    };

    const fetchScores = useCallback(async (filters) => {
        if (reportIds.length < 2) {
            setRadarData({ pillars: [], series: [], noData: true });
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const activeFilters = buildActiveFilters(filters);
            const result = await insightsApi.getRiskPrediction(reportIds, activeFilters);
            const perReport = waves.map((w) => ({ w, scores: result[String(w.report_id)] ?? {} }));
            const firstWithData = perReport.find((x) => Object.keys(x.scores).length > 0);
            const pillars = firstWithData ? Object.keys(firstWithData.scores) : [];

            if (pillars.length === 0) {
                setRadarData({ pillars: [], series: [], noData: true });
            } else {
                setRadarData({
                    pillars,
                    series: perReport.map((x, i) => ({
                        label: x.w.label,
                        color: x.w.color ?? colorForIndex(i),
                        // RISK, [0, 1], higher is worse — what the radar plots.
                        values: normalizeForRadar(x.scores, pillars),
                        // The same numbers before conversion: SIGNAL, [-1, 1],
                        // higher is better — what the bar view plots. Held
                        // alongside so the switch never triggers a refetch.
                        signals: pillars.map((pl) => Number(x.scores[pl] ?? 0)),
                    })),
                    noData: false,
                });
            }
        } catch {
            setError(intl.formatMessage({
                id: "projects.risk_fetch_error",
                defaultMessage: "Failed to load risk predictions.",
            }));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportIds, intl]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            fetchScores({});
            return;
        }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchScores(selectedFilters), 2000);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters]);

    const handleToggle = (key) => (_, next) => {
        setSelectedFilters((prev) => ({ ...prev, [key]: next ?? [] }));
    };

    // Open the modal and stream a fresh comparison explanation for the CURRENT
    // radar slice every time. Meta ({ wave1, wave2 }) lands first so the header
    // chips render immediately; then tokens append live (typewriter). Because we
    // regenerate on each open, the modal always reflects the live filters.
    const handleOpenInsight = () => {
        if (waves.length !== 2) return;

        // Tear down any in-flight stream from a previous open.
        if (streamAbortRef.current) {
            streamAbortRef.current();
            streamAbortRef.current = null;
        }

        setInsightOpen(true);
        setInsightLoading(true);
        setInsightError(null);
        setInsightData(null);

        streamAbortRef.current = insightsApi.streamRiskInsightCompare(
            waves[0].report_id,
            waves[1].report_id,
            buildActiveFilters(selectedFilters),
            {
                onMeta: (meta) => {
                    setInsightData((prev) => ({
                        ...(prev || {}),
                        ...meta,
                        explanation: prev?.explanation || '',
                        streaming: true,
                    }));
                },
                onToken: (t) => {
                    setInsightData((prev) => ({
                        ...(prev || {}),
                        explanation: (prev?.explanation || '') + t,
                        streaming: true,
                    }));
                },
                onDone: () => {
                    setInsightLoading(false);
                    setInsightData((prev) => (prev ? { ...prev, streaming: false } : prev));
                    streamAbortRef.current = null;
                },
                onError: () => {
                    setInsightLoading(false);
                    setInsightError(intl.formatMessage({
                        id: "projects.compare_insight_error",
                        defaultMessage: "Failed to generate the comparison insight.",
                    }));
                    streamAbortRef.current = null;
                },
            }
        );
    };

    const handleCloseInsight = () => {
        if (streamAbortRef.current) {
            streamAbortRef.current();
            streamAbortRef.current = null;
        }
        setInsightOpen(false);
        setInsightLoading(false);
        setInsightData(null);
        setInsightError(null);
    };

    // Abort any in-flight stream if the component unmounts mid-generation.
    useEffect(() => () => {
        if (streamAbortRef.current) streamAbortRef.current();
    }, []);

    // A small overall-risk band per wave, derived from the mean of that wave's
    // pillar scores when the meta carries them. The meta scores are SIGNAL
    // scores in [-1, 1] (higher is better), so convert the mean to the [0, 1]
    // risk scale before banding — the chip is labelled as risk. Used only for
    // the optional header chips, so it degrades gracefully when scores are absent.
    const waveBand = (wave) => {
        const scores = wave && wave.scores;
        if (!scores || typeof scores !== "object") return null;
        const vals = Object.values(scores).map(Number).filter((v) => !Number.isNaN(v));
        if (vals.length === 0) return null;
        return riskBand(signalToRisk(vals.reduce((a, b) => a + b, 0) / vals.length));
    };

    const canExplain = waves.length === 2 && (radarData?.pillars?.length ?? 0) > 0;

    const toggleBtnSx = {
        flexWrap: "wrap",
        gap: 0.75,
        "& .MuiToggleButton-root": {
            textTransform: "none",
            fontWeight: 600,
            px: 1.5,
            borderRadius: 1.75,
            borderColor: "rgba(21, 101, 192, 0.2)",
            color: "text.secondary",
            fontSize: 12,
        },
        "& .MuiToggleButton-root.Mui-selected": {
            bgcolor: "rgba(21, 101, 192, 0.12)",
            color: "#0d47a1",
            borderColor: "rgba(21, 101, 192, 0.45)",
        },
    };

    return (
        <Box
            sx={{
                background: "linear-gradient(135deg, #f8faff 0%, #eef3fb 100%)",
                borderRadius: 3,
                border: "1px solid rgba(21, 101, 192, 0.12)",
                p: { xs: 2, sm: 3 },
                boxShadow: "0 8px 18px rgba(13, 71, 161, 0.06)",
            }}
        >
            <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ alignItems: "flex-start" }}>
                {/* Filter panel — only shown if riskFilters data is available */}
                {filterKeys.length > 0 && (
                    <Box
                        sx={{
                            minWidth: { xs: "100%", md: 280 },
                            p: 2,
                            borderRadius: 2.5,
                            background: "rgba(255,255,255,0.7)",
                            border: "1px solid rgba(21, 101, 192, 0.12)",
                        }}
                    >
                        <Stack spacing={2.5}>
                            {filterKeys.map((key) => (
                                <FormControl key={key} component="fieldset" variant="standard" sx={{ m: 0 }}>
                                    <FormLabel
                                        component="legend"
                                        sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", mb: 1, textTransform: "capitalize" }}
                                    >
                                        {intl.formatMessage({
                                            id: `projects.risk_filter_${key}`,
                                            defaultMessage: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
                                        })}
                                    </FormLabel>
                                    <ToggleButtonGroup
                                        value={selectedFilters[key] || []}
                                        onChange={handleToggle(key)}
                                        exclusive={false}
                                        sx={toggleBtnSx}
                                    >
                                        {(riskFilters[key] || []).map((opt) => (
                                            <ToggleButton key={opt} value={opt} size="small">
                                                {opt}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </FormControl>
                            ))}
                            <Typography variant="caption" color="text.secondary">
                                <FormattedMessage
                                    id="projects.risk_filter_hint"
                                    defaultMessage="Leave all unselected to include everyone. Changes apply after 2 seconds."
                                />
                            </Typography>
                        </Stack>
                    </Box>
                )}

                {/* Chart */}
                <Box sx={{ flex: 1, minWidth: 600, display: "flex", flexDirection: "column" }}>
                    {/* "Insights by AI" trigger — only for a two-wave radar with data */}
                    {!loading && !error && canExplain && (
                        <Stack direction="row" sx={{ alignSelf: "flex-end", mb: -1 }}>
                            <Tooltip
                                title={intl.formatMessage({
                                    id: "projects.compare_insight_open",
                                    defaultMessage: "Explain this comparison with AI",
                                })}
                                placement="left"
                            >
                                {/* span keeps the tooltip working while the button is disabled */}
                                <span>
                                    <IconButton
                                        onClick={handleOpenInsight}
                                        disabled={insightLoading}
                                        aria-label={intl.formatMessage({
                                            id: "projects.compare_insight_open",
                                            defaultMessage: "Explain this comparison with AI",
                                        })}
                                        sx={{
                                            p: 1.25,
                                            color: "#1565c0",
                                            transition:
                                                "color 0.15s ease, background-color 0.15s ease, transform 0.15s ease",
                                            "&:hover": {
                                                color: "#0d47a1",
                                                bgcolor: "rgba(21, 101, 192, 0.12)",
                                                transform: "scale(1.08)",
                                            },
                                        }}
                                    >
                                        {insightLoading ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            <TipsAndUpdatesOutlinedIcon sx={{ fontSize: 28 }} />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    )}

                    {loading && (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 220 }}>
                            <CircularProgress size={32} />
                        </Box>
                    )}
                    {!loading && error && (
                        <Alert severity="error">{error}</Alert>
                    )}
                    {!loading && !error && radarData?.noData && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 220,
                                gap: 1.5,
                                color: "text.disabled",
                            }}
                        >
                            <AssessmentOutlinedIcon sx={{ fontSize: 56, opacity: 0.35 }} />
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                <FormattedMessage
                                    id="projects.risk_no_data"
                                    defaultMessage="No risk data available for the selected waves."
                                />
                            </Typography>
                        </Box>
                    )}
                    {!loading && !error && radarData && !radarData.noData && radarData.pillars.length > 0 && (
                        <>
                            {/* Radar ⇄ bars. Both read the same prediction
                                scores, on opposite scales. */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                sx={{ justifyContent: "center", mb: 1 }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: showBars ? "text.secondary" : "#00438E",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => setShowBars(false)}
                                >
                                    <FormattedMessage id="projects.risk_view_radar" defaultMessage="Radar" />
                                </Typography>
                                <Switch
                                    checked={showBars}
                                    onChange={(e) => setShowBars(e.target.checked)}
                                    inputProps={{
                                        "aria-label": intl.formatMessage({
                                            id: "projects.risk_view_switch_label",
                                            defaultMessage: "Switch between the radar and the pillar bars",
                                        }),
                                    }}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#00438E" },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                            bgcolor: "#00438E",
                                        },
                                        "& .MuiSwitch-switchBase": { color: "#00438E" },
                                        "& .MuiSwitch-track": { bgcolor: "#90a4ae" },
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: showBars ? "#00438E" : "text.secondary",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => setShowBars(true)}
                                >
                                    <FormattedMessage id="projects.risk_view_bars" defaultMessage="Bars" />
                                </Typography>
                            </Stack>

                            {showBars ? (
                                <CompareSignalBars
                                    pillars={radarData.pillars}
                                    series={radarData.series}
                                />
                            ) : (
                                <RiskRadarChart
                                    pillars={radarData.pillars}
                                    series={radarData.series}
                                />
                            )}
                        </>
                    )}
                </Box>
            </Stack>

            {/* AI comparison-insight modal — regenerated for the current slice on each open */}
            <Dialog
                open={insightOpen}
                onClose={handleCloseInsight}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: { borderRadius: 3, border: "1px solid rgba(21, 101, 192, 0.12)" },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        pr: 6,
                        fontWeight: 700,
                        color: "#0d47a1",
                        background: "linear-gradient(135deg, #f8faff 0%, #eef3fb 100%)",
                    }}
                >
                    <TipsAndUpdatesOutlinedIcon fontSize="small" sx={{ color: "#1565c0" }} />
                    <FormattedMessage id="projects.compare_insight_title" defaultMessage="AI Risk Comparison" />
                    {waves.length === 2 && (
                        <Box component="span" sx={{ fontWeight: 600, color: "#1565c0" }}>
                            {`— ${waves[0].label} vs ${waves[1].label}`}
                        </Box>
                    )}
                    {/* Optional per-wave overall-risk status chips (from meta) */}
                    {insightData && (
                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
                            {[
                                { wave: insightData.wave1, label: waves[0]?.label },
                                { wave: insightData.wave2, label: waves[1]?.label },
                            ].map(({ wave, label }, i) => {
                                const band = waveBand(wave);
                                if (!band || !label) return null;
                                return (
                                    <Chip
                                        key={i}
                                        size="small"
                                        label={`${label}: ${band.label}`}
                                        sx={{
                                            bgcolor: band.bg,
                                            color: band.color,
                                            border: `1px solid ${band.border}`,
                                            fontWeight: 600,
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    )}
                    <IconButton
                        aria-label={intl.formatMessage({ id: "projects.compare_insight_close", defaultMessage: "Close" })}
                        onClick={handleCloseInsight}
                        size="small"
                        sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {/* Spinner only until the first stream frame lands */}
                    {insightLoading && !insightData && !insightError && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1.5,
                                py: 5,
                            }}
                        >
                            <CircularProgress size={28} />
                            <Typography variant="body2" color="text.secondary">
                                <FormattedMessage
                                    id="projects.compare_insight_loading"
                                    defaultMessage="Generating comparison…"
                                />
                            </Typography>
                        </Box>
                    )}

                    {insightError && <Alert severity="error">{insightError}</Alert>}

                    {/* The AI explanation (markdown, GFM tables/task-lists), streamed live;
                        a blinking cursor trails while tokens arrive */}
                    {!insightError && insightData && (insightData.explanation || insightData.streaming) && (
                        <Box
                            sx={{
                                fontSize: 15,
                                lineHeight: 1.7,
                                color: "text.primary",
                                overflowX: "auto",
                                "& p": { mt: 0, mb: 1.5 },
                                "& p:last-child": { mb: 0 },
                                "& ul, & ol": { mt: 0, mb: 1.5, pl: 3 },
                                "& li": { mb: 0.5 },
                                "& table": { width: "100%", borderCollapse: "collapse", fontSize: 13, my: 1 },
                                "& th, & td": { border: "0.5px solid rgba(21,101,192,0.15)", padding: "6px 10px", textAlign: "left" },
                                "& th": { background: "rgba(21,101,192,0.06)", color: "#0d47a1", fontWeight: 500 },
                                "@keyframes risk-cursor-blink": { "50%": { opacity: 0 } },
                            }}
                        >
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{insightData.explanation}</ReactMarkdown>
                            {insightData.streaming && (
                                <Box
                                    component="span"
                                    aria-hidden="true"
                                    sx={{
                                        display: "inline-block",
                                        ml: "1px",
                                        color: "#1565c0",
                                        fontWeight: 700,
                                        animation: "risk-cursor-blink 1s step-end infinite",
                                    }}
                                >
                                    ▍
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button
                        onClick={handleCloseInsight}
                        sx={{ textTransform: "none", fontWeight: 600, color: "#0d47a1" }}
                    >
                        <FormattedMessage id="projects.compare_insight_close" defaultMessage="Close" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ── Compare Insight Chat ──────────────────────────────────────────────────────
const getWsUrl = (httpUrl) => {
    if (httpUrl.startsWith("https://")) return httpUrl.replace("https://", "wss://");
    if (httpUrl.startsWith("http://")) return httpUrl.replace("http://", "ws://");
    return httpUrl;
};

const QUICK_PROMPTS = [
    {
        id: "summary",
        icon: SummarizeIcon,
        labelId: "projects.quick_prompt_summary_label",
        labelDefault: "Summary",
        descriptionId: "projects.quick_prompt_summary_desc",
        descriptionDefault: "Concise overview of how the waves compare.",
        color: "#1565c0",
        bg: "rgba(21, 101, 192, 0.06)",
        border: "rgba(21, 101, 192, 0.18)",
    },
    {
        id: "risk_summary",
        icon: ReportProblemOutlinedIcon,
        labelId: "projects.quick_prompt_risk_summary_label",
        labelDefault: "Risk Summary",
        descriptionId: "projects.quick_prompt_risk_summary_desc",
        descriptionDefault: "Wave-by-wave risk levels and their key drivers.",
        color: "#c62828",
        bg: "rgba(198, 40, 40, 0.06)",
        border: "rgba(198, 40, 40, 0.18)",
        // Routes the WS frame to the BE's risk stream (promptId + scope:'risk'),
        // which answers from risk data and returns a risk_meta frame.
        isRisk: true,
    },
    {
        id: "change_plan",
        icon: TrendingUpIcon,
        labelId: "projects.quick_prompt_change_plan_label",
        labelDefault: "Change Plan",
        descriptionId: "projects.quick_prompt_change_plan_desc",
        descriptionDefault: "Structured action plan based on observed changes.",
        color: "#2e7d32",
        bg: "rgba(46, 125, 50, 0.06)",
        border: "rgba(46, 125, 50, 0.18)",
    },
    {
        id: "comms_plan",
        icon: PsychologyIcon,
        labelId: "projects.quick_prompt_comms_label",
        labelDefault: "Communication Plan",
        descriptionId: "projects.quick_prompt_comms_desc",
        descriptionDefault: "Communication and engagement strategy for the changes.",
        color: "#6a1b9a",
        bg: "rgba(106, 27, 154, 0.06)",
        border: "rgba(106, 27, 154, 0.18)",
    },
    {
        id: "training_plan",
        icon: EmojiObjectsIcon,
        labelId: "projects.quick_prompt_training_label",
        labelDefault: "Training Plan",
        descriptionId: "projects.quick_prompt_training_desc",
        descriptionDefault: "Training recommendations based on capability gaps.",
        color: "#e65100",
        bg: "rgba(230, 81, 0, 0.06)",
        border: "rgba(230, 81, 0, 0.18)",
    },
    {
        id: "leader_talking_points",
        icon: TrendingUpIcon,
        labelId: "projects.quick_prompt_leader_tp_label",
        labelDefault: "Leader Talking Points",
        descriptionId: "projects.quick_prompt_leader_tp_desc",
        descriptionDefault: "Tailored talking points, by audience, about leadership.",
        color: "#00695c",
        bg: "rgba(0, 105, 92, 0.06)",
        border: "rgba(0, 105, 92, 0.18)",
    },
];

// Exported for tests: the landing state (quick-prompt cards, no messages yet)
// and the active state share one input row, and a test renders this directly
// to lock the settings gear into it.
export function CompareInsightChat({ compareResults, projectId }) {
    const intl = useIntl();
    const [userInput, setUserInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    // The agent's current lookup ({ tool, label }) from the BE's `status`
    // frames, shown in the loading state until the answer starts
    const [agentStatus, setAgentStatus] = useState(null);
    const [error, setError] = useState(null);
    // Risk mode: latched by the Risk Summary quick prompt so follow-up
    // questions keep answering from risk data (the BE's risk Q&A variant),
    // mirroring the single-report Ask Prompta chat.
    const [riskMode, setRiskMode] = useState(false);
    const [llmProvider, setLlmProvider] = useChatLlmProvider();

    // Which sources the next question is answered from. `null` means
    // everything, which is where every new conversation starts. Local state on
    // purpose: nothing should remember it across conversations, and the store
    // is per-project chat history, not per-turn settings.
    //
    // This is the surface where wave selection bites hardest — the comparison
    // chat genuinely retrieves across every wave in `reportIds`, so narrowing
    // `wave_report_ids` here changes what is searched, not just what is context.
    const [ragSources, setRagSources] = useState(null);
    const [selectedSources, setSelectedSources] = useState(null);

    // Deselecting every source leaves nothing to answer from. The backend
    // refuses such a turn too; this stops it from ever being sent, and says the
    // same thing the backend would have. Only meaningful once the selector is
    // offering something — before that there is nothing to deselect.
    const noSourcesSelected =
        hasSources(ragSources) && selectedCount(selectedSources, ragSources) === 0;
    const noSourcesMessage = intl.formatMessage({
        id: 'chatSources.none_selected',
        defaultMessage: 'Select at least one source to search.',
    });

    // The risk prompts answer from the prediction endpoint, so they are only
    // offered when risk_filters gives it something to work with.
    const quickPrompts = useMemo(
        () => (hasRiskFilters(compareResults?.data?.risk_filters)
            ? QUICK_PROMPTS
            : QUICK_PROMPTS.filter((qp) => !qp.isRisk)),
        [compareResults]
    );
    const websocketRef = useRef(null);
    // paces a cached answer through the streamed path — see chunkReplay
    const replayRef = useRef(null);

    // Stop the current answer: close its socket and drop the rest of a cached
    // answer's replay, so either kind of answer is interrupted the same way.
    // Forgetting both refs is what stops it for good: a stream's handlers act
    // only while it is still the current one (see sendMessage).
    const stopStream = () => {
        replayRef.current?.cancel();
        replayRef.current = null;
        const ws = websocketRef.current;
        websocketRef.current = null;
        ws?.close();
        setAgentStatus(null);
    };

    // Oldest wave first: `waves` is built from the BE's reports list sorted by
    // `order`, which is the order the risk stream expects (wave 1 → wave N).
    const reportIds = useMemo(
        () => (compareResults?.waves || []).map((w) => w.report_id),
        [compareResults]
    );
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [chatSessions, setChatSessions] = useState([]);
    const channel = 'project_insights_generation';
    const askState = useStore((state) => state.askPrompta[`${projectId}::${channel}`]);
    const initSession = useStore((state) => state.initAskPromptaSession);
    const messagesFromStore = askState?.messages || [];
    const messages = messagesFromStore;
    const qaHistory = askState?.qaHistory || [];
    const sessionId = askState?.sessionId;
    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
    const [isViewingHistory, setIsViewingHistory] = useState(false);
    const [downloadAnchor, setDownloadAnchor] = useState(null);
    const [downloadTarget, setDownloadTarget] = useState(null);
    const appendUserMessage = useStore((state) => state.appendUserMessage);
    const appendQaHistoryEntry = useStore((state) => state.appendQaHistoryEntry);
    const appendAssistantChunk = useStore((state) => state.appendAssistantChunk);
    const appendAssistantMessage = useStore((state) => state.appendAssistantMessage);
    const attachAssistantRiskMeta = useStore((state) => state.attachAssistantRiskMeta);
    const finalizeAssistantMessage = useStore((state) => state.finalizeAssistantMessage);
    const loadHistorySession = useStore((state) => state.loadHistorySession);
    const resetAskPrompta = useStore((state) => state.resetAskPrompta);

    // ---- scroll helpers ----
    // Same behaviour as the single-report chat: follow the stream only while the
    // user is parked at the bottom, so scrolling up to read mid-answer isn't
    // yanked back down by the next token.
    const scrollContainerRef = useRef(null);
    const isAtBottom = useRef(true);

    // Scroll the chat box itself rather than scrollIntoView() on a marker: the
    // latter also scrolls every ancestor, which dragged the whole page down on
    // each streamed token. Instant, not smooth — a smooth scroll keeps firing
    // scroll events from mid-animation positions, which handleScroll would read
    // as "the user scrolled away" and switch following off on its own.
    const scrollToBottom = () => {
        const el = scrollContainerRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    };

    const handleScroll = () => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const threshold = 40;
        isAtBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    };

    useEffect(() => () => stopStream(), []);

    // isLoading is a trigger too: the spinner mounts on its own when a quick
    // prompt starts streaming without appending a user message first.
    useEffect(() => {
        if (isAtBottom.current) scrollToBottom();
    }, [messages, isLoading]);

    // Opening a history session should always land at the bottom.
    useEffect(() => {
        if (isViewingHistory && messages.length > 0) {
            isAtBottom.current = true;
            scrollToBottom();
        }
    }, [isViewingHistory, messages.length]);

    useEffect(() => {
        if (projectId && !askState) {
            initSession(projectId, channel);
        }
    }, [projectId, askState]);

    // What the Sources section may offer. Sources are project-scoped and this
    // chat knows its project outright, so it asks by project id rather than
    // going through a report. A failure just leaves the section unrendered,
    // which is the pre-feature popover rather than a broken chat.
    useEffect(() => {
        if (!projectId) return;
        let cancelled = false;
        insightsApi
            .getRagSources(null, { project_id: projectId })
            .then((data) => {
                if (!cancelled) setRagSources(data);
            })
            .catch((err) => {
                console.error("Failed to fetch RAG sources for the selector:", err);
                if (!cancelled) setRagSources(null);
            });
        return () => {
            cancelled = true;
        };
    }, [projectId]);

    const buildHistoryForRAG = () =>
        qaHistory.slice(-5).map((item) => ({
            question: item.question,
            answer: item.answer,
        }));

    const sendMessage = useCallback((question, { silent = false, promptId = null, scope = null } = {}) => {
        if (((!question || !question.trim()) && !promptId) || isLoading) return;
        // Nothing selected means nothing to answer from. Send is disabled for
        // it, and this is the chokepoint Enter and the quick prompts share.
        if (noSourcesSelected) return;

        // `undefined` while the selection still covers everything available.
        const sources = buildSourcesPayload(selectedSources, ragSources);

        if (!silent) {
            const displayText = promptId ? `[${promptId}]` : question;
            appendUserMessage(projectId, channel, displayText);
        }

        // stopStream(), inline so it stays out of the useCallback deps
        replayRef.current?.cancel();
        const previous = websocketRef.current;
        websocketRef.current = null;
        previous?.close();
        const replay = createChunkReplay((chunk) => {
            const currentMessages =
                useStore.getState().askPrompta[`${projectId}::${channel}`]?.messages || [];

            if (
                currentMessages.length === 0 ||
                !currentMessages[currentMessages.length - 1]?.isStreaming
            ) {
                appendAssistantMessage(projectId, channel);
            }

            appendAssistantChunk(projectId, channel, chunk);
        });
        replayRef.current = replay;

        const accessToken = localStorage.getItem(token_keys.access_token);
        const backendBase =
            process.env.REACT_APP_BACKEND_URL ||
            (process.env.NODE_ENV === "development" ? "http://localhost:8000" : "");
        const ws = new WebSocket(
            `${getWsUrl(backendBase)}/ws/rag/`,
            ["Bearer", accessToken]
        );
        websocketRef.current = ws;
        setAgentStatus(null);
        setIsLoading(true);

        ws.onopen = () => {
            ws.send(
                JSON.stringify({
                    session_id: sessionId,
                    report_ids: reportIds,
                    channel: channel,
                    // the risk stream expects null, not '', for a quick prompt
                    user_input: question || null,
                    promptId: promptId,
                    history: buildHistoryForRAG(),
                    report_metrics: compareResults?.data ?? {},
                    llm_provider: llmProvider,
                    // Absent unless the user narrowed the sources, so the
                    // default request stays exactly the one that predates the
                    // selector. This chat sends no top-level `document_ids`;
                    // the nested object is what the backend reads either way.
                    ...(sources && { sources }),
                    ...(scope && { scope }),
                })
            );
        };

        // A stopped stream's frames must never reach the (new) chat
        const isCurrent = () => websocketRef.current === ws;

        ws.onmessage = (event) => {
            if (!isCurrent()) return;
            const data = JSON.parse(event.data);

            // A lookup the agent started (or null once it finished): loading
            // state only, never part of the answer
            if ('status' in data) {
                setAgentStatus(data.status);
                return;
            }

            // Risk summary: risk_meta (per-report scores/drivers) arrives before
            // the answer tokens. Store it on the assistant message so the score
            // cards and radars render above the insight — never into the text.
            if (data.risk_meta) {
                attachAssistantRiskMeta(projectId, channel, data.risk_meta);
            }

            // A quick-prompt cache hit: the answer that follows is typed out
            // by the replay rather than shown at once (see chunkReplay)
            if (data.cached) {
                replay.markCached();
                return;
            }

            if (data.done) {
                ws.close();
                setAgentStatus(null);
                // a cached answer may still be being revealed — finish it first
                replay.end(() => {
                    finalizeAssistantMessage(projectId, channel);
                    setIsLoading(false);
                });
                return;
            }

            if (!data.message) return;

            const chunk = data.message
                .split("\n\n")
                .map((p) => p.replace("data: ", ""))
                .join("")
                .replaceAll("$n$l$", "\n");

            replay.push(chunk);
        };

        ws.onerror = () => {
            if (!isCurrent()) return;
            setError(
                intl.formatMessage({
                    id: "projects.compare_chat_error",
                    defaultMessage: "Connection error. Please try again.",
                })
            );
            setIsLoading(false);
        };

        ws.onclose = (event) => {
            if (!isCurrent()) return;
            // Risk answers may close without a `done` frame — finalize on close
            // so the message closes and the spinner clears. Scoped to risk only;
            // normal answers keep the unclean-only path.
            if (scope === 'risk') {
                replay.end(() => {
                    finalizeAssistantMessage(projectId, channel);
                    setIsLoading(false);
                });
                return;
            }
            if (!event.wasClean) setIsLoading(false);
        };
    }, [isLoading, reportIds, compareResults, intl, sessionId, qaHistory, llmProvider,
        noSourcesSelected, selectedSources, ragSources]);

    const handleSend = () => {
        const question = userInput.trim();
        if (!question) return;
        setUserInput("");
        // In risk mode the question goes to the risk stream's Q&A variant:
        // same promptId, with the question as user_input.
        sendMessage(question, riskMode ? { promptId: "risk_summary", scope: "risk" } : {});
    };

    const loadProjectChatHistory = async () => {
        try {
            setLoadingHistory(true);
            setChatSessions(await insightsApi.getProjectChatHistorySessions(projectId, channel));
        } catch {
            setError(intl.formatMessage({ id: 'askPrompta.load_history_failed', defaultMessage: 'Failed to load project chat history' }));
        } finally {
            setLoadingHistory(false);
        }
    };

    const loadProjectSessionMessages = async (sessionId) => {
        try {
            stopStream();
            setIsLoading(true);

            const rows = await insightsApi.getProjectChatSessionMessages(sessionId, channel);

            const msgs = [];
            const history = [];

            rows.forEach(r => {
                msgs.push({ role: 'user', content: r.question });
                msgs.push({ role: 'assistant', content: r.answer });
                history.push({ question: r.question, answer: r.answer });
            });

            loadHistorySession(projectId, channel, sessionId, msgs, history);

            setSelectedSessionId(sessionId);
            setIsViewingHistory(true);
            setHistoryDrawerOpen(false);

        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (projectId) loadProjectChatHistory();
        return () => {
            stopStream();
            resetAskPrompta(projectId, channel);
        }
    }, [projectId]);

    useEffect(() => {
        if (historyDrawerOpen && projectId) loadProjectChatHistory();
    }, [historyDrawerOpen, projectId]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleDownloadClick = (event, msg, index) => {
        setDownloadAnchor(event.currentTarget);
        setDownloadTarget({ content: msg.content, index, riskMeta: msg.riskMeta });
    };

    const handleDownloadClose = () => {
        setDownloadAnchor(null);
        setDownloadTarget(null);
    };

    const downloadAsPdf = async () => {
        if (!downloadTarget) return;
        const { content, index, riskMeta } = downloadTarget;
        handleDownloadClose();
        await exportAnswerAsPdf(content, `comparison-answer-${index + 1}-${exportTimestamp()}.pdf`, { riskMeta });
    };

    const downloadAsWord = async () => {
        if (!downloadTarget) return;
        const { content, index, riskMeta } = downloadTarget;
        handleDownloadClose();
        await exportAnswerAsWord(content, `comparison-answer-${index + 1}-${exportTimestamp()}.docx`, { riskMeta });
    };

    const handleQuickPrompt = (qp) => {
        if (isLoading) return;
        setRiskMode(!!qp.isRisk);
        // Quick prompts render no user bubble, so nothing seeds a qaHistory
        // slot — without one the streamed answer accumulates onto the previous
        // entry's answer. Seed it, keyed by the prompt id as the question.
        appendQaHistoryEntry(projectId, channel, qp.id);
        sendMessage("", { silent: true, promptId: qp.id, scope: qp.isRisk ? "risk" : null });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}

        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    mb: 1,
                    px: 0.5,
                }}
            >
                <Box sx={{ display: "flex", width: "100%", flexDirection: "row", justifyContent: "space-between", gap: 1 }}>
                    <Chip
                        label={intl.formatMessage({ id: "compare.viewing_history", defaultMessage: "Close Chat" })}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onDelete={() => {
                            // abort an answer still streaming or typing out,
                            // then start clean
                            stopStream();
                            setIsLoading(false);
                            setIsViewingHistory(false);
                            setSelectedSessionId(null);
                            setRiskMode(false);
                            initSession(projectId, channel);
                        }}
                        sx={{
                            height: 32,
                            px: 1,
                            '& .MuiChip-label': {
                                px: 1
                            }
                        }}
                    />
                    <Button
                        startIcon={<HistoryIcon />}
                        size="small"
                        variant="outlined"
                        onClick={() => setHistoryDrawerOpen(true)}
                    >
                        {intl.formatMessage({ id: "askPrompta.history", defaultMessage: "History" })}
                    </Button>



                </Box>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {/* Quick-prompt cards — only shown before the conversation starts */}
            {messages.length === 0 && !isLoading && (
                <Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1.5,
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                        >
                            <FormattedMessage
                                id="projects.quick_prompts_hint"
                                defaultMessage="Select a starting point or ask your own question below."
                            />
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1.5,
                        }}
                    >
                        {quickPrompts.map((qp) => {
                            const Icon = qp.icon;
                            return (
                                <Paper
                                    key={qp.id}
                                    elevation={0}
                                    onClick={() => handleQuickPrompt(qp)}
                                    sx={{
                                        p: 2,
                                        cursor: "pointer",
                                        border: `1px solid ${qp.border}`,
                                        background: qp.bg,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1.5,
                                        transition: "all 0.15s ease",
                                        "&:hover": {
                                            borderColor: qp.color,
                                            background: qp.bg,
                                            boxShadow: `0 2px 8px ${qp.border}`,
                                            transform: "translateY(-1px)",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 1.5,
                                            bgcolor: qp.bg,
                                            border: `1px solid ${qp.border}`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 18, color: qp.color }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: qp.color, lineHeight: 1.3 }}>
                                            <FormattedMessage id={qp.labelId} defaultMessage={qp.labelDefault} />
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: "block", mt: 0.25 }}>
                                            <FormattedMessage id={qp.descriptionId} defaultMessage={qp.descriptionDefault} />
                                        </Typography>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* Loading spinner when a quick prompt fires before the first reply */}
            {messages.length === 0 && isLoading && (
                <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                    <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                        <TypingIndicator showStatus status={agentStatus} />
                    </Paper>
                </Box>
            )}

            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {messages.length > 0 && (
                <Box
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    sx={{
                        maxHeight: 420,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0,
                    }}
                >
                    {messages.map((msg, i) => (
                        <Box
                            key={i}
                            sx={{
                                mb: 2,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: msg.role === "user" ? "flex-end" : "flex-start",
                            }}
                        >
                            <Paper
                                elevation={1}
                                sx={{
                                    p: 1.5,
                                    // risk answers carry a full set of score cards
                                    // per wave — they need the full bubble width
                                    maxWidth: msg.riskMeta ? "100%" : "82%",
                                    borderRadius: 2,
                                    bgcolor: msg.role === "user" ? "#1565c0" : "background.paper",
                                    color: msg.role === "user" ? "#fff" : "text.primary",
                                    "& p": { m: 0 },
                                    "& p + *": { mt: 1 },
                                    "& ul, & ol": { pl: 2, my: 0.5 },
                                    // Answers routinely come back as GFM tables; without the
                                    // borders they read as a wall of pipes. Same styling as
                                    // the comparison-insight modal above and the single-report
                                    // chat, and the bubble scrolls a wide table on its own.
                                    ...(msg.role === "assistant" && {
                                        overflowX: "auto",
                                        "& table": { width: "100%", borderCollapse: "collapse", fontSize: 13, my: 1 },
                                        "& th, & td": { border: "0.5px solid rgba(21,101,192,0.15)", padding: "6px 10px", textAlign: "left" },
                                        "& th": { background: "rgba(21,101,192,0.06)", color: "#0d47a1", fontWeight: 500 },
                                    }),
                                }}
                            >
                                {/* Risk-summary score cards, above the streamed insight */}
                                {msg.role === "assistant" && msg.riskMeta && (
                                    <RiskMetaPanels riskMeta={msg.riskMeta} />
                                )}
                                {/* remarkGfm is what actually turns the pipe rows into a
                                    <table> — plain react-markdown leaves them as text */}
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                                {msg.role === "assistant" && msg.isStreaming && (
                                    <span style={{ marginLeft: 4, opacity: 0.45 }}>▊</span>
                                )}
                            </Paper>
                            {msg.role === "assistant" && !msg.isStreaming && msg.content && (
                                <Tooltip title={intl.formatMessage({ id: "askPrompta.download", defaultMessage: "Export" })}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleDownloadClick(e, msg, i)}
                                        aria-label={intl.formatMessage({ id: "askPrompta.download", defaultMessage: "Export" })}
                                        sx={{
                                            mt: 0.5,
                                            width: 28,
                                            height: 28,
                                            color: "text.disabled",
                                            "&:hover": { bgcolor: "action.hover", color: "text.secondary" },
                                        }}
                                    >
                                        <DownloadIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    ))}

                    {isLoading && !messages.some((m) => m.isStreaming) && (
                        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
                            <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                                <TypingIndicator showStatus status={agentStatus} />
                            </Paper>
                        </Box>
                    )}
                </Box>
            )}

            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                <ChatSettingsButton
                    provider={llmProvider}
                    onChange={setLlmProvider}
                    disabled={isLoading}
                    sourcesAvailable={ragSources}
                    sourcesValue={selectedSources}
                    onSourcesChange={setSelectedSources}
                />
                <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    size="small"
                    variant="outlined"
                    placeholder={intl.formatMessage({
                        id: "projects.compare_chat_input",
                        defaultMessage: "Ask about this comparison…",
                    })}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                />
                <Tooltip title={noSourcesSelected ? noSourcesMessage : ""}>
                    <span>
                        <IconButton
                            onClick={handleSend}
                            disabled={!userInput.trim() || isLoading || noSourcesSelected}
                            sx={{
                                bgcolor: "#1565c0",
                                color: "#fff",
                                borderRadius: 1.5,
                                p: 1,
                                "&:hover": { bgcolor: "#003A75" },
                                "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                            }}
                        >
                            <SendIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <AnswerExportMenu
                anchorEl={downloadAnchor}
                onClose={handleDownloadClose}
                onPdf={downloadAsPdf}
                onWord={downloadAsWord}
            />
            <Drawer
                anchor="right"
                open={historyDrawerOpen}
                onClose={() => setHistoryDrawerOpen(false)}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: { xs: "100%", sm: 400 },
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <Typography variant="h6" sx={{ display: "flex", gap: 1 }}>
                            <HistoryIcon />
                            {intl.formatMessage({ id: "askPrompta.chat_history", defaultMessage: "Chat History" })}
                        </Typography>

                        <IconButton onClick={() => setHistoryDrawerOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {loadingHistory ? (
                        <CircularProgress />
                    ) : (
                        <List>
                            {chatSessions.map((session, index) => (
                                <React.Fragment key={session.session_id}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => {
                                                if (session.count > 1) {
                                                    loadProjectSessionMessages(session.sessions[0].session_id);
                                                }
                                                else {
                                                    loadProjectSessionMessages(session.session_id);
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <ChatIcon sx={{ fontSize: 18 }} />
                                                <Typography variant="body2">
                                                    {session.first_question}
                                                </Typography>
                                            </Box>
                                        </ListItemButton>
                                    </ListItem>
                                    {index < chatSessions.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>
            </Drawer>
        </Paper>
    );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function CompareResultsPage() {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { projectId } = useParams();
    const location = useLocation();

    const [compareResults, setCompareResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [compareTab, setCompareTab] = useState(DEFAULT_TAB);
    const [assessmentView, setAssessmentView] = useState(ASSESSMENT_VIEWS[0].key);
    const [chartsOpen, setChartsOpen] = useState(false);

    const fetchProjectData = useCallback(async () => {
        if (!projectId) return;
        try {
            setLoading(true);
            // Same payload shape the compare endpoint returned — reports[],
            // pillars[], keywords[], risk_filters{} — but addressed by project,
            // so every wave in the project is included and the URL no longer
            // carries an ordered list of report ids.
            const data = await insightsApi.getProject(projectId);
            const projectID = data?.project_id ?? projectId;
            const state = location.state;
            let project = state?.project ?? null;

            // The payload carries no project name, so seed it from navigation
            // state and fall back to the projects list. Report names come with
            // the assessments; an older BE sends none, and a wave whose
            // assessment is not linked has none, so those fall back too.
            const nameById = {};
            (state?.waves || []).forEach((w) => {
                nameById[String(w.report_id)] = w.report_name;
            });
            (data?.assessments || []).forEach((a) => {
                (a.reports || []).forEach((r) => {
                    if (r.report_name) nameById[String(r.report_id)] = r.report_name;
                });
            });

            const metaList = Array.isArray(data?.reports)
                ? [...data.reports].sort((a, b) => a.order - b.order)
                : [];

            if (!project || Object.keys(nameById).length < metaList.length) {
                const projectsData = await insightsApi.getProjects({ page_size: 100 });
                const match = (projectsData.results || []).find(
                    (p) => String(p.id) === String(projectId)
                );
                if (match) {
                    project = project ?? match;
                    (match.waves || []).forEach((w) => {
                        if (!(String(w.report_id) in nameById)) {
                            nameById[String(w.report_id)] = w.report_name;
                        }
                    });
                }
            }

            const waves = metaList.map((m, i) => ({
                report_id: m.report_id,
                wave_count: m.wave_count,
                label: m.label || `Wave ${m.wave_count}`,
                report_name: nameById[String(m.report_id)] || `Report ${m.report_id}`,
                color: colorForIndex(i),
            }));

            // The detail payload carries the caller's role; the card (from
            // navigation state or the list) is the fallback for an older BE.
            const myRole = data?.my_role !== undefined ? data.my_role : project?.my_role;

            // Each assessment's reports become its waves, so a row's report
            // buttons carry the wave label and chart colour. An older BE sends
            // no assessments: list each wave as its own row, as before.
            const waveById = Object.fromEntries(waves.map((w) => [String(w.report_id), w]));
            const assessments = Array.isArray(data?.assessments)
                ? data.assessments.map((a) => ({
                      ...a,
                      key: `assessment-${a.assessment_id}`,
                      reports: (a.reports || [])
                          .map((r) => waveById[String(r.report_id)])
                          .filter(Boolean),
                  }))
                : waves.map((w) => ({ key: `report-${w.report_id}`, title: w.report_name, reports: [w] }));

            setCompareResults({ data, waves, assessments, project, projectID, myRole });
        } catch (error) {
            console.error("Failed to load project:", error);
            enqueueSnackbar(
                intl.formatMessage({ id: "projects.load_failed", defaultMessage: "Failed to load project" }),
                { variant: "error" }
            );
            navigate("/projects", { replace: true });
        } finally {
            setLoading(false);
        }
    }, [projectId, location.state, enqueueSnackbar, intl, navigate]);

    useEffect(() => {
        fetchProjectData();
    }, [fetchProjectData]);

    const handleBack = () => {
        navigate("/projects");
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
                <CircularProgress sx={{ color: "#00438E" }} />
            </Box>
        );
    }

    if (!compareResults) {
        return null;
    }

    const waves = compareResults.waves || [];
    // Insight Generation sits on the right, after every left-hand tab.
    const insightGenTab = LEFT_TABS.length;

    // Risk needs risk_filters to slice by; without them the option is dropped
    // rather than shown leading to an empty chart.
    const showRisk = hasRiskFilters(compareResults?.data?.risk_filters);
    const assessmentViews = showRisk
        ? ASSESSMENT_VIEWS
        : ASSESSMENT_VIEWS.filter((v) => v.key !== "risk");
    // Guard against a stale selection: risk must not survive as the active view
    // into a payload that no longer offers it.
    const activeView = assessmentViews.some((v) => v.key === assessmentView)
        ? assessmentView
        : assessmentViews[0].key;

    return (
        <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", gap: 2 }}>
                <IconButton
                    onClick={handleBack}
                    sx={{
                        color: "#00438E",
                        "&:hover": { backgroundColor: "rgba(0, 67, 142, 0.08)" },
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: "#00438E" }}>
                        <FormattedMessage
                            id="projects.project_title"
                            defaultMessage="Project: {project}"
                            values={{ project: compareResults.project?.name ?? "" }}
                        />
                    </Typography>
                </Box>
            </Box>

            {/* Tabs */}
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    mb: 2,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                }}
            >
                <Tabs
                    value={compareTab < LEFT_TABS.length ? compareTab : false}
                    onChange={(_, v) => setCompareTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ "& .MuiTab-root": { fontWeight: 600 } }}
                >
                    {LEFT_TABS.map((t) => (
                        <Tab
                            key={t.key}
                            label={intl.formatMessage({ id: t.labelId, defaultMessage: t.label })}
                        />
                    ))}
                </Tabs>
                <Tabs
                    value={compareTab === insightGenTab ? 0 : false}
                    onChange={() => setCompareTab(insightGenTab)}
                    sx={{
                        "& .MuiTab-root": { fontWeight: 600 },
                        "& .MuiTabs-indicator": { bgcolor: "#00438E" },
                    }}
                >
                    <Tab
                        label={intl.formatMessage({
                            id: "projects.insight_generation_tab",
                            defaultMessage: "Insight Generation",
                        })}
                    />
                </Tabs>
            </Box>

            {/* Content */}
            {/* Assessments are the only evidence we
                gather today, so this lists them directly, each with its report. */}
            {LEFT_TABS[compareTab]?.key === "evidence" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <AssessmentList
                        assessments={compareResults.assessments || []}
                        canEdit={canEditProjectContent(compareResults?.myRole)}
                        projectId={compareResults?.projectID}
                    />
                    <Box>
                        {/* Charts stay folded away until asked for. */}
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => setChartsOpen((o) => !o)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setChartsOpen((o) => !o);
                                }
                            }}
                            aria-expanded={chartsOpen}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                cursor: "pointer",
                                userSelect: "none",
                                borderRadius: 1,
                                px: 0.5,
                                py: 0.25,
                                mb: chartsOpen ? 2 : 0,
                                "&:hover": { bgcolor: "rgba(0, 67, 142, 0.06)" },
                            }}
                        >
                            <ExpandMoreIcon
                                sx={{
                                    fontSize: 22,
                                    color: "#00438E",
                                    transform: chartsOpen ? "none" : "rotate(-90deg)",
                                    transition: "transform 150ms",
                                }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#00438E" }}>
                                <FormattedMessage
                                    id="projects.charts_section"
                                    defaultMessage="Charts"
                                />
                            </Typography>
                        </Box>

                        <Collapse in={chartsOpen} unmountOnExit>
                            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={activeView}
                                    onChange={(_, v) => v && setAssessmentView(v)}
                                    sx={{
                                        "& .MuiToggleButton-root": {
                                            px: 2,
                                            fontWeight: 600,
                                            textTransform: "none",
                                            color: "#00438E",
                                            borderColor: "rgba(0, 67, 142, 0.3)",
                                            "&.Mui-selected": {
                                                bgcolor: "rgba(0, 67, 142, 0.1)",
                                                color: "#00438E",
                                                "&:hover": { bgcolor: "rgba(0, 67, 142, 0.16)" },
                                            },
                                        },
                                    }}
                                >
                                    {assessmentViews.map((v) => (
                                        <ToggleButton key={v.key} value={v.key}>
                                            {intl.formatMessage({ id: v.labelId, defaultMessage: v.label })}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>

                            {activeView === "pillar" && (
                                <PillarBarChart
                                    pillars={compareResults?.data?.pillars ?? []}
                                    waves={waves}
                                />
                            )}
                            {activeView === "top_terms" && (
                                <KeywordBumpChart
                                    keywords={compareResults?.data?.keywords ?? []}
                                    waves={waves}
                                />
                            )}
                            {activeView === "risk" && (
                                <CompareRisksPanel
                                    waves={waves}
                                    riskFilters={compareResults?.data?.risk_filters ?? null}
                                />
                            )}
                        </Collapse>
                    </Box>
                </Box>
            )}
            {LEFT_TABS[compareTab]?.key === "documents" && (
                <DocumentsPage
                    projectId={projectId}
                    canEdit={canEditProjectContent(compareResults?.myRole)}
                />
            )}
            {/* The Impact Register — stakeholder groups and their impacts —
                scoped to this project. It takes no `waves`: the register is
                filled before any assessment exists, so nothing in it is
                wave-scoped. */}
            {LEFT_TABS[compareTab]?.key === "impacts" && (
                <ImpactsWorkspacePage
                    projectId={projectId}
                    projectName={compareResults.project?.name}
                    canEdit={canEditProjectContent(compareResults?.myRole)}
                />
            )}
            {compareTab < LEFT_TABS.length &&
                !["impacts", "evidence", "documents"].includes(LEFT_TABS[compareTab].key) && (
                <EmptyPanel
                    label={intl.formatMessage({
                        id: LEFT_TABS[compareTab].labelId,
                        defaultMessage: LEFT_TABS[compareTab].label,
                    })}
                />
            )}
            {compareTab === insightGenTab && (
                <CompareInsightChat
                    key={waves.map((w) => w.report_id).join("-")}
                    compareResults={compareResults}
                    projectId={compareResults?.projectID}
                />
            )}
        </Box>
    );
}
